import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../components/SearchInput';
import ProjectsGrid from '../components/ProjectsGrid';
import NewsletterPopup from '../components/NewsletterPopup'; // Import the popup component
import NewsletterSubscription from '../components/NewsletterSubscription'; // Import the subscription component
import { t } from '../i18n'; // Import translation function
import FooterBar from '../components/FooterBar';
import '../App.css';

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [sortBy, setSortBy] = useState('likes'); // 'name', 'likes', or 'difficulty'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [showPopup, setShowPopup] = useState(false); // State to control popup visibility
  const [stats, setStats] = useState({ objects: 0, projects: 0, users: 0 });
  const [likedProjectIds, setLikedProjectIds] = useState([]); // State to store liked project IDs
  const navigate = useNavigate();

  // Utility to check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Fetch statistics for the statistics section
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/statistics`)
      .then((response) => response.json())
      .then((data) => {
        setStats({
          objects: data.objects || 0,
          projects: data.projects || 0,
          users: data.users || 0,
        });
      })
      .catch((error) => {
        console.error('Error fetching statistics:', error);
        setStats({ objects: 0, projects: 0, users: 0 });
      });
  }, []);
  // Simulate user login state (replace with actual logic)
  const token = localStorage.getItem('token'); // Retrieve the token from local storage

  // Utility to get API base URL depending on environment
  const getApiBaseUrl = () => {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return 'http://localhost:5000'; // Use backend port for local dev
    }
    // Use relative URL for production
    return '';
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/projects`)
      .then((response) => response.json())
      .then((data) => {
        // If the API returns { projects: [...] }, use data.projects; otherwise, use data if it's an array
        if (Array.isArray(data)) {
          setProjects(data);
        } else if (Array.isArray(data.projects)) {
          setProjects(data.projects);
        } else {
          setProjects([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
        setProjects([]);
      });
  }, []);
  // Sort projects based on sortBy and sortDirection
  const sortedProjects = [...projects].sort((a, b) => {
    let result = 0;
    if (sortBy === 'name') {
      result = a.name.localeCompare(b.name);
    } else if (sortBy === 'likes') {
      result = (a.likes || 0) - (b.likes || 0);
    } else if (sortBy === 'difficulty') {
      // Sort by difficulty (assume lower is easier)
      result = (a.difficulty || 0) - (b.difficulty || 0);
    }
    return sortDirection === 'asc' ? result : -result;
  });

  // Handler for clicking a sort label
  const handleSortClick = (field) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    // Set a timeout to show the popup after 30 seconds
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 30000); // 30 seconds

    return () => clearTimeout(timer); // Cleanup the timer on component unmount
  }, []);

  // Utility to create a slug from a string
  function slugify(text) {
    return text
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase();
  }

  const handleProjectClick = (idOrIdSlug) => {
    let id = idOrIdSlug;
    let slug = '';
    if (typeof idOrIdSlug === 'string' && idOrIdSlug.includes('-')) {
      // Already in id-slug format
      navigate(`/project/${idOrIdSlug}`);
      return;
    }
    // Find project by id to get name
    const project = projects.find((p) => p.id === idOrIdSlug || p.id === Number(idOrIdSlug));
    if (project) {
      slug = slugify(project.name);
      navigate(`/project/${id}-${slug}`);
    } else {
      navigate(`/project/${id}`);
    }
  };

  // --- Liked project logic for logged-in users ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${getApiBaseUrl()}/api/users/liked-projects`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => response.ok ? response.json() : { projects: [] })
      .then((data) => {
        setLikedProjectIds(Array.isArray(data.projects) ? data.projects.map((p) => p.id) : []);
      })
      .catch(() => setLikedProjectIds([]));
  }, []);

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://0123456789.fr/" />
      </Helmet>
      <div style={styles.page}>

        {/* Main Title - Responsive */}
        <div className="catchPhraseContainer">
          <h1 className="catchPhrase">{t('siteTitle') || 'Pour des objets de Zéro à Neuf !'}</h1>
          <div className="catchPhraseSub">
            {t('siteCatchPhrase') || "Détournez l'usage de vos objets cassés pour leur redonner vie"}
          </div>
        </div>
        <div style={{ height: 16 }} />

        {/* Hero sections styled as project cards, responsive */}
        <div className="projectContainer" style={styles.heroCard}>
          <div className="projectInfo" style={styles.heroInfo}>
            <div className="projectName" style={styles.heroTitle}>
              {t('searchObjectTitle') || "J'ai un objet à sauver"}
            </div>
            <div style={styles.heroInputWrapper}>
              <SearchInput placeholder={t('searchPlaceholder') || 'Chaise, lampe, …'} />
            </div>
          </div>
        </div>
        <div className="projectContainer" style={{ ...styles.heroCard, marginBottom: 32 }}>
          <div className="projectInfo" style={styles.heroInfo}>
            <div className="projectName" style={styles.heroTitle}>
              {t('projectTitle') || "J'ai un projet pour sauver des objets"}
            </div>
            <button
              className="heroButtonCustom"
              style={styles.heroButton}
              onClick={() => navigate('/create-project')}
            >
              {t('projectButton') || 'Je partage mon idée !'}
            </button>
          </div>
        </div>

        {/* How it works Section - styled as hero card */}
        <div className="projectContainer" style={{ ...styles.heroCard, marginBottom: 32 }}>
          <div className="projectInfo" style={styles.heroInfo}>
            <div className="projectName" style={styles.heroTitle}>
              {t('howItWorksTitle') || 'Comment ça marche ?'}
            </div>
            <ol style={{
              textAlign: 'left',
              fontSize: '1.15rem',
              margin: 0,
              paddingLeft: 24,
              color: '#333',
              width: '100%',
              maxWidth: 350,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              <li style={{ marginBottom: 10, lineHeight: 1.6 }}>
                {t('howItWorksStep1') || 'Trouvez un objet (cassé ou inutile)'}
              </li>
              <li style={{ marginBottom: 10, lineHeight: 1.6 }}>
                {t('howItWorksStep2') || 'Découvrez des idées proposées par la communauté'}
              </li>
              <li style={{ marginBottom: 10, lineHeight: 1.6 }}>
                {t('howItWorksStep3') || 'Partagez votre propre transformation'}
              </li>
            </ol>
          </div>
        </div>

        {/* Statistics Section - styled as hero card */}
        <div className="projectContainer" style={{ ...styles.heroCard, marginBottom: 32 }}>
          <div style={styles.statsRow}>
            <div style={styles.statsCol}>
              <div style={styles.statsNumber}>{stats.objects}</div>
              <div style={styles.statsLabel}>{t('statsObjects') || 'objets sauvés'}</div>
            </div>
            <div style={styles.statsCol}>
              <div style={styles.statsNumber}>{stats.projects}</div>
              <div style={styles.statsLabel}>{t('statsProjects') || 'idées partagées'}</div>
            </div>
            <div style={styles.statsCol}>
              <div style={styles.statsNumber}>{stats.users}</div>
              <div style={styles.statsLabel}>{t('statsUsers') || 'contributeurs'}</div>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div style={styles.projectsSection}>
          <h2 style={styles.sectionTitle}>{t('rescuedObjectsTitle') || 'Ils étaient cassés, ils ont repris la vague'}</h2>
          {/* Sorting Labels */}
          <div style={styles.sortRow}>
            <span style={styles.sortLabel}>Trier par&nbsp;:</span>
            <span
              style={{
                ...styles.sortOption,
                ...(sortBy === 'likes' ? styles.activeSortOption : {}),
                cursor: 'pointer',
              }}
              onClick={() => handleSortClick('likes')}
            >
              Likes&nbsp;
              {sortBy === 'likes' && (
                <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
              )}
            </span>
            <span
              style={{
                ...styles.sortOption,
                ...(sortBy === 'name' ? styles.activeSortOption : {}),
                cursor: 'pointer',
                marginLeft: '16px',
              }}
              onClick={() => handleSortClick('name')}
            >
              Nom&nbsp;
              {sortBy === 'name' && (
                <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
              )}
            </span>
            <span
              style={{
                ...styles.sortOption,
                ...(sortBy === 'difficulty' ? styles.activeSortOption : {}),
                cursor: 'pointer',
                marginLeft: '16px',
              }}
              onClick={() => handleSortClick('difficulty')}
            >
              Difficulté&nbsp;
              {sortBy === 'difficulty' && (
                <span style={styles.arrow}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
              )}
            </span>
          </div>
          {/* Display projects in chunks of 6, inserting a button after each chunk except the last */}
          {(() => {
            const chunks = [];
            for (let i = 0; i < sortedProjects.length; i += 6) {
              chunks.push(sortedProjects.slice(i, i + 6));
            }
            return chunks.map((chunk, idx) => (
              <React.Fragment key={idx}>
                <ProjectsGrid
                  projects={chunk}
                  onProjectClick={handleProjectClick}
                  likedProjectIds={isLoggedIn ? likedProjectIds : []}
                  onLikeRequireLogin={() => {
                    if (!isLoggedIn) navigate('/create-user');
                  }}
                  updateLikedProjectIds={(projectId) => {
                    setLikedProjectIds((prev) => [...prev, projectId]);
                  }}
                />
                {idx < chunks.length - 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '32px 0' }}>
                    <button
                      style={{
                        padding: '12px 28px',
                        fontSize: '18px',
                        backgroundColor: 'var(--green-eucalyptus, #2ecc40)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(46,204,64,0.08)'
                      }}
                      onClick={() => navigate('/create-project')}
                    >
                      Partager ma création
                    </button>
                  </div>
                )}
              </React.Fragment>
            ));
          })()}
        </div>

        {/* Newsletter Section */}
        <div style={styles.newsletterSection}>
          <NewsletterSubscription isLoggedIn={isLoggedIn} token={token} />
        </div>

        {/* Newsletter Popup */}
        {showPopup && <NewsletterPopup isLoggedIn={isLoggedIn} token={token} />}
      </div>
      {/* FooterBar with language switcher, only on HomePage */}
      <FooterBar />
    </>
  );
};

const styles = {
  statsRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxWidth: 350,
    margin: '0 auto',
    gap: 12,
  },
  statsCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 0',
  },
  statsNumber: {
    fontSize: '1.7rem',
    fontWeight: 700,
    color: 'var(--green-eucalyptus, #2ecc40)',
    marginBottom: 2,
    letterSpacing: 1,
  },
  statsLabel: {
    fontSize: '1rem',
    color: '#333',
    textAlign: 'center',
    fontWeight: 500,
  },
  page: {
    position: 'relative',
    textAlign: 'center',
    fontFamily: `'Poppins', sans-serif`,
    paddingTop: '15px',
    width: '100vw',
    margin: 0,
    boxSizing: 'border-box',
    minHeight: '100vh',
    background: '#f7f7f7',
  },
  catchPhraseContainer: {
    width: '100%',
    maxWidth: 900,
    margin: '0 auto',
    padding: '0 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 80,
  },
  // catchPhrase style moved to App.css for real media queries
  heroCard: {
    marginBottom: 24,
    maxWidth: 500,
    marginLeft: 'auto',
    marginRight: 'auto',
    height: 'auto',
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 12px 0 rgba(46,204,64,0.08)',
    padding: '24px 18px 18px 18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'box-shadow 0.2s',
  },
  heroInfo: {
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: 'auto',
    display: 'flex',
  },
  heroTitle: {
    fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 0,
    color: '#222',
    textAlign: 'center',
    lineHeight: 1.25,
  },
  heroInputWrapper: {
    width: '100%',
    maxWidth: 350,
    marginBottom: 5,
    marginTop: 12,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  heroButton: {
    background: 'var(--green-eucalyptus, #2ecc40)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    fontSize: 16,
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: 6,
    marginBottom: 6,
    transition: 'background 0.2s, filter 0.2s',
    width: '100%',
    maxWidth: 260,
    boxShadow: '0 1px 6px 0 rgba(46,204,64,0.08)',
  },
  projectsSection: {
    marginTop: 40,
    padding: '0 20px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  sortRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    marginTop: 18,
    gap: 10,
    minHeight: 40,
    width: '100%',
  },
  sortLabel: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  sortOption: {
    fontSize: 16,
    padding: '4px 10px',
    borderRadius: 5,
    border: '1px solid transparent',
    transition: 'background 0.2s, border 0.2s',
    userSelect: 'none',
  },
  activeSortOption: {
    background: '#fff',
    border: '2px solid var(--green-eucalyptus, #2ecc40)',
    fontWeight: 'bold',
    color: 'var(--green-eucalyptus, #2ecc40)',
    boxShadow: '0 1px 6px 0 rgba(46,204,64,0.08)',
  },
  arrow: {
    fontSize: 14,
    verticalAlign: 'middle',
  },
  sectionTitle: {
    marginTop: 60,
    fontSize: 'clamp(1.3rem, 3vw, 2rem)',
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#333',
    textAlign: 'center',
  },
  newsletterSection: {
    marginTop: 40,
    padding: 20,
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    maxWidth: 800,
    margin: '40px auto',
  },
  // Responsive styles for mobile
  '@media (max-width: 700px)': {
    catchPhrase: {
      fontSize: '1.3rem',
      marginTop: 12,
    },
    heroCard: {
      padding: '16px 6px 12px 6px',
      maxWidth: '98vw',
    },
    heroTitle: {
      fontSize: '1rem',
    },
    projectsSection: {
      padding: '0 2vw',
      maxWidth: '100vw',
    },
    sectionTitle: {
      fontSize: '1.1rem',
      marginTop: 32,
    },
  },
};

export default HomePage;

// Add CSS for hover effect
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  .heroButtonCustom:hover {
    background-color: #27ae60 !important;
    filter: brightness(0.95);
    transition: background 0.2s, filter 0.2s;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(styleSheet);
}